import { useState, useEffect, useRef, useReducer } from 'react';
import * as Cesium from "cesium";
import './App.css';

import WebSocketService from './services/WebSocketService';
import { messageReducer } from './services/messageReducer'; // Import the reducer as it cannot be accessed before initialization
import { WalkthroughProvider, useWalkthrough } from './services/WalkthroughContext';

import IntroScreen from './components/IntroScreen.jsx';
import componentRegistry from './services/componentRegistry';
import CesiumControls from './components/CesiumControls';
import TerrainButtons from './components/TerrainButtons';

import { mercatorToLonLat } from './utils/MercatorToLonLat';
import SpeechBubbleChat from './components/SpeechBubbleChat';

// This token is public facing and only enabled for the ECHO project (echodemo.com), so it's safe to include here
// Cesium.Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJiNGNmY2VjOC1jMzc2LTQ4NmYtYjY5Zi0xYWE1Zjg1NDZhMTkiLCJpZCI6MjY4NjYzLCJpYXQiOjE3Mzg2MzI2MjN9.kC6VNV79IeSV3SatTDqZoSWWBOu-Lrq5BkBo6Nye9M8"
// Cesium.Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJiMDQxMjk2Ny1mMGVhLTRhNDEtODZiMi0wNGEwZmMyNzIyODkiLCJpZCI6Mjc2NzA1LCJpYXQiOjE3Mzk4MDIwNjJ9.7Uk2DuOx1i2-Bo96m3di6AERsXTcbGRLgXHZVw2oVGY"
Cesium.Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJkMDlhMzJkOC0wNzQ5LTQ5MWItODc0Yi0yYTIyZTZlNjczNTQiLCJpZCI6MjY4NjYzLCJpYXQiOjE3NjM0Nzc3MzJ9.V1XOD9I-rnk9msn01FVRuoag_jlsUSiMXs_MyZXQs1Q"

function App() {
  const [isMobile, setIsMobile] = useState(false);

  // -- SpeechBubble
  const [showBubble, setShowBubble] = useState(false);
  const [messages, dispatchMessage] = useReducer(messageReducer, [])
  const [showImageModal, setShowImageModal] = useState(false);
  // -- ProjectBubble
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState("New Chat");
  const [showFolder, setShowFolder] = useState(false);

  // CesiumControls
  // -- LayerBox
  const [layerProgress, setLayerProgress] = useState(0);
  const [showLayerBox, setShowLayerBox] = useState(false);
  const [formattedLayers, setFormattedLayers] = useState([])
  const [layerDate, setLayerDate] = useState(['06/03/2025'])
  const [layerList, setLayerList] = useState([])
  // -- TimestampLayerBox
  const [showTimestampLayerBox, setShowTimestampLayerBox] = useState(true);
  const timestampTilesPath = "/kmltiles/WD_RAIN104431@all_painted"
  const extent_x_min = 619106.2451016440754756;
  const extent_x_max = 630394.2451016440754756;
  const extent_y_min = 6551378.9224481238052249;
  const extent_y_max = 6558193.9224481238052249;
  var [view_west, view_south] = mercatorToLonLat(extent_x_min, extent_y_min);
  var [view_east, view_north] = mercatorToLonLat(extent_x_max, extent_y_max);
  const timestampLayers = [
      { ts: 3600, name: "3600", url: `${timestampTilesPath}/WD_RAIN104431@3600_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 7200, name: "7200", url: `${timestampTilesPath}/WD_RAIN104431@7200_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 10800, name: "10800", url: `${timestampTilesPath}/WD_RAIN104431@10800_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 14400, name: "14400", url: `${timestampTilesPath}/WD_RAIN104431@14400_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 18000, name: "18000", url: `${timestampTilesPath}/WD_RAIN104431@18000_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 21600, name: "21600", url: `${timestampTilesPath}/WD_RAIN104431@21600_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 25200, name: "25200", url: `${timestampTilesPath}/WD_RAIN104431@25200_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 28800, name: "28800", url: `${timestampTilesPath}/WD_RAIN104431@28800_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 32400, name: "32400", url: `${timestampTilesPath}/WD_RAIN104431@32400_painted`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
  ];
  const [timestampLayerSources, setTimestampLayerSources] = useState({});
  // for (var layer of timestampLayers) {
  //     // url: `${timestampTilesPath}/WD_RAIN104431@${layer.name}_painted`
  //     layer.url = `${timestampTilesPath}/WD_RAIN104431@${layer.name}_painted`
  // }
  const [timestampLayerVisibility, setTimestampLayerVisibility] = useState(
      Object.fromEntries(timestampLayers.map(l => [l.ts, false]))  // set all layers to invisible
  );
  const [timestampLabels, setTimestampLabels] = useState([]);
  const riverWatermaskTilesPath = "/kmltiles/river_watermask"
  const riverWatermaskLayers = [
    { ts: 0, name: "0", url: `${riverWatermaskTilesPath}/`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
  ]
  const [riverWatermaskLayerSources, setRiverWatermaskLayerSources] = useState({});
  const [riverWatermaskLayerVisibility, setRiverWatermaskLayerVisibility] = useState(
      Object.fromEntries(riverWatermaskLayers.map(l => [l.ts, false]))  // set all layers to invisible
  );
  // -- Routes Layers
  const [showRoutesLayerBox, setShowRoutesLayerBox] = useState(true);
  const routesGeoJsonPath = "/data/routes"
  const routesLayers = [
      // Test route
      // { ts: 3600, name: "3600", url: `${routesGeoJsonPath}/test/2025-11-12_16-59-38.447288_routes_ts_3600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // Initial routes
      // { ts: 3600, name: "3600", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-35-36.578393_routes_ts_3600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 7200, name: "7200", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-36-14.687999_routes_ts_7200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 10800, name: "10800", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-36-55.771345_routes_ts_10800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 14400, name: "14400", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-37-37.210956_routes_ts_14400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 18000, name: "18000", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-38-20.572358_routes_ts_18000.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 21600, name: "21600", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-39-04.232146_routes_ts_21600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 25200, name: "25200", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-40-06.765601_routes_ts_25200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 28800, name: "28800", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-41-07.222276_routes_ts_28800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // { ts: 32400, name: "32400", url: `${routesGeoJsonPath}/initial_source_sinks/2025-11-17_17-41-55.672262_routes_ts_32400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      // Tall building sink routes
      { ts: 3600, name: "3600", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-57-49.077439_routes_ts_3600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 7200, name: "7200", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-58-12.215707_routes_ts_7200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 10800, name: "10800", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-58-34.069990_routes_ts_10800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 14400, name: "14400", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-58-56.205629_routes_ts_14400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 18000, name: "18000", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-59-19.058824_routes_ts_18000.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 21600, name: "21600", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_17-59-44.302323_routes_ts_21600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 25200, name: "25200", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_18-00-14.096812_routes_ts_25200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 28800, name: "28800", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_18-00-43.973222_routes_ts_28800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 32400, name: "32400", url: `${routesGeoJsonPath}/tall_building_routes/2025-11-18_18-01-09.450113_routes_ts_32400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
  ];
  const [routesLayerSources, setRoutesLayerSources] = useState({});
  const [routesLayerVisibility, setRoutesLayerVisibility] = useState(
      Object.fromEntries(routesLayers.map(l => [l.ts, false]))  // set all layers to invisible
  );
  // -- Helicopter Routes Layers
  const [showHelicopterRoutesLayerBox, setShowHelicopterRoutesLayerBox] = useState(true);
  const helicopterRoutesLayers = [
      // Helicopter evac routes
      { ts: 0, name: "0", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_21-18-49.915515_initial_helicopter_evac_routes_ts_0.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 3600, name: "3600", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-42-33.606471_helicopter_evac_routes_ts_3600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 7200, name: "7200", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-42-47.035822_helicopter_evac_routes_ts_7200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 10800, name: "10800", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-43-01.021352_helicopter_evac_routes_ts_10800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 14400, name: "14400", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-43-14.791514_helicopter_evac_routes_ts_14400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 18000, name: "18000", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-43-29.262166_helicopter_evac_routes_ts_18000.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 21600, name: "21600", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-43-45.862842_helicopter_evac_routes_ts_21600.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 25200, name: "25200", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-44-06.010316_helicopter_evac_routes_ts_25200.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 28800, name: "28800", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-44-29.344821_helicopter_evac_routes_ts_28800.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
      { ts: 32400, name: "32400", url: `${routesGeoJsonPath}/helicopter_evac_routes/2025-11-20_19-44-45.827893_helicopter_evac_routes_ts_32400.geojson`, min_x: 8445, max_x: 8449, min_y: 10870, max_y: 10873, extent: [view_west, view_south, view_east, view_north] },
  ];
  const [helicopterRoutesLayerSources, setHelicopterRoutesLayerSources] = useState({});
  const [helicopterRoutesLayerVisibility, setHelicopterRoutesLayerVisibility] = useState(
      Object.fromEntries(helicopterRoutesLayers.map(l => [l.ts, false]))  // set all layers to invisible
  );
  const [selectedTimestamp, setSelectedTimestamp] = useState(timestampLayers[0].ts);
  const [selectedLayerType, setSelectedLayerType] = useState("Flood");

  // -- CloudControlBox
  const [showCloudsBox, setShowCloudsBox] = useState(false);
  const [cloudsEnabled, setCloudsEnabled] = useState(false);
  const [cloudOpacity, setCloudOpacity] = useState(0.25);
  const [cloudThreshold, setCloudThreshold] = useState(0.4);
  const [cloudRange, setCloudRange] = useState(0.15);
  const [cloudSteps, setCloudSteps] = useState(80);

  // -- RainControlBox
  const [showRainBox, setShowRainBox] = useState(false);
  const [rainEnabled, setRainEnabled] = useState(false);
  const [rainIntensity, setRainIntensity] = useState(1.0);
  const [rainSpeed, setRainSpeed] = useState(1.0);
  const [rainBlur, setRainBlur] = useState(1.0);

  // -- WindControlBox
  const [showWindBox, setShowWindBox] = useState(false);
  const [windEnabled, setWindEnabled] = useState(false);
  const [windSpeed, setWindSpeed] = useState(10.0);
  const [windDirection, setWindDirection] = useState(45.0);
  const [windParticleDensity, setWindParticleDensity] = useState(2000);
  const [windAltitude, setWindAltitude] = useState(1000);
  const [windTurbulence, setWindTurbulence] = useState(0.3);

  // -- GPUWindControlBox
  const [showGpuWindBox, setShowGpuWindBox] = useState(false);
  const [gpuWindEnabled, setGpuWindEnabled] = useState(false);
  const [gpuWindSpeed, setGpuWindSpeed] = useState(10.0);
  const [gpuWindDirection, setGpuWindDirection] = useState(45.0);
  const [gpuWindParticleCount, setGpuWindParticleCount] = useState(10000);
  const [gpuWindAltitude, setGpuWindAltitude] = useState(1000);
  const [gpuWindTurbulence, setGpuWindTurbulence] = useState(0.3);
  const [gpuWindParticleLifetime, setGpuWindParticleLifetime] = useState(12.0);
  const [gpuWindParticleSize, setGpuWindParticleSize] = useState(6.0);

  // Generics
  const viewerRef = useRef();
  const cesiumControlsRef = useRef();
  // let dataSources = [];

  const [externalPrompt, setExternalPrompt] = useState("");
  const [sessionKey, setSessionKey] = useState("");
  const [ws, setWs] = useState(null);

  const [currentTimestampLayerIndex, setCurrentTimestampLayerIndex] = useState(0);
  const [isAnimatingTimestamps, setIsAnimatingTimestamps] = useState(false);
  const animationIntervalRef = useRef(null);

  const [currentHelicopterRoutesLayerIndex, setCurrentHelicopterRoutesLayerIndex] = useState(null);

  const [showFloodTimestamps, setShowFloodTimestamps] = useState(false);
  const [showHelicopterEvacRoutes, setShowHelicopterEvacRoutes] = useState(false);
  const [showTallBuildings, setShowTallBuildings] = useState(false);
  const [currentWalkthroughStep, setCurrentWalkthroughStep] = useState(null);

const flyToLocation = (coords, height = 1000, orientation) => {
  if (viewerRef.current?.cesiumElement) {
    const viewer = viewerRef.current.cesiumElement;
    const dest = Cesium.Cartesian3.fromDegrees(coords[1], coords[0], height);
    const options = { destination: dest, duration: 2 };
    if (orientation) {
      options.orientation = orientation;
    }
    viewer.camera.flyTo(options);
  }
};

  // Flood timestamp animation functions
  const startTimestampAnimation = () => {
    // Stop any existing animation
    stopTimestampAnimation();

    // Hide all current timestamps and show the first one
    setTimestampLayerVisibility(
      Object.fromEntries(timestampLayers.map((l, idx) => [l.ts, idx === 0]))
    );

    // Start animation from the first timestamp
    setCurrentTimestampLayerIndex(0);
    setIsAnimatingTimestamps(true);
  };

  const stopTimestampAnimation = () => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    setIsAnimatingTimestamps(false);
  };

  // Sequential toggle function for smooth animation transitions
  const sequentialToggleTimestamp = async (fromIndex, toIndex) => {
    // Hide the current timestamp layer if one is visible
    if (fromIndex >= 0 && fromIndex < timestampLayers.length) {
      const currentLayer = timestampLayers[fromIndex];
      if (timestampLayerVisibility[currentLayer.ts]) {
        setTimestampLayerVisibility(prev => ({
          ...prev,
          [currentLayer.ts]: false
        }));
        // Small delay to let the hide operation complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Show the next timestamp layer
    if (toIndex >= 0 && toIndex < timestampLayers.length) {
      const nextLayer = timestampLayers[toIndex];
      setTimestampLayerVisibility(prev => ({
        ...prev,
        [nextLayer.ts]: true
      }));
    }
  };

  // Animation effect - cycles through timestamps when isAnimatingTimestamps is true
  useEffect(() => {
    if (!isAnimatingTimestamps) {
      // Clean up interval when animation stops
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      return;
    }

    // Start the animation interval
    animationIntervalRef.current = setInterval(async () => {
      setCurrentTimestampLayerIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % timestampLayers.length;

        // Use sequential toggle for smooth transitions
        sequentialToggleTimestamp(prevIndex, nextIndex);

        return nextIndex;
      });
    }, 2000); // Change timestamp every 2 seconds

    // Cleanup on unmount or when animation stops
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [isAnimatingTimestamps, timestampLayers, timestampLayerVisibility]);

  // UI handler invoked by WalkthroughContext (via handlers.ui)
  const handleUiAction = async (action) => {
    if (!action) return;
    const a = action.action || action;
    switch (a.type) {
      case 'showLoading':
        // temp loading message
        dispatchMessage({ type: 'ADD_MESSAGE', message: { type: 'text', role: 'agent', data: a.label || 'Loading...', attachments: [] } });
        break;
      case 'hideLoading':
        // remove last 'Loading...' agent message
        dispatchMessage({ type: 'SET_MESSAGES', messages: messages.filter(m => !(m.role === 'agent' && (m.data || '').toString().includes('Loading'))) });
        break;
      case 'enableClouds':
        if (typeof a.enabled !== 'undefined') {
          setCloudsEnabled(!!a.enabled);
        }
        break;
      case 'enableRain':
        if (typeof a.enabled !== 'undefined') {
          setRainEnabled(!!a.enabled);
        }
        break;
      case 'flyTo':
        // Allow UI actions to trigger the same flyTo handler
        try {
          if (a.coords) {
            // support optional orientation or orientationDegrees
            let orientation = undefined;
            if (a.orientation && typeof a.orientation === 'object') {
              orientation = a.orientation; // assume radians
            } else if (a.orientationDegrees && typeof a.orientationDegrees === 'object') {
              const d2r = Math.PI / 180.0;
              orientation = {
                heading: (a.orientationDegrees.heading || 0) * d2r,
                pitch: (a.orientationDegrees.pitch || 0) * d2r,
                roll: (a.orientationDegrees.roll || 0) * d2r,
              };
            }
            flyToLocation(a.coords, a.height, orientation);
          }
        } catch (e) {
          console.warn('flyTo failed', e);
        }
        break;
      case 'showTimestamps':
        if (typeof a.enabled !== 'undefined') {
          setShowFloodTimestamps(a.enabled);

          // Handle auto-animation
          if (a.enabled && a.autoAnimate) {
            // Start automatic timestamp animation
            startTimestampAnimation();
          } else if (!a.enabled) {
            // Stop animation when timestamps are disabled
            stopTimestampAnimation();
            // Optional: hide all timestamp layers when disabled
            if (a.hideLayers) {
              setTimestampLayerVisibility(
                Object.fromEntries(timestampLayers.map(l => [l.ts, false]))
              );
            }
          }
        }
      //   try {
      //     // Show timestamp labels and set Cesium timeline range
      //     const startISO = a.startISO || new Date().toISOString();
      //     const startDate = new Date(startISO);
      //     const labels = timestampLayers.map(l => {
      //       const dt = new Date(startDate.getTime() + l.ts * 1000);
      //       return { ts: l.ts, iso: dt.toISOString(), label: dt.toLocaleString() };
      //     });
      //     setTimestampLabels(labels);
      //     // make all timestamp layers visible by default when showing timestamps
      //     setTimestampLayerVisibility(Object.fromEntries(timestampLayers.map(l => [l.ts, true])));

      //     // update Cesium clock/timeline if viewer exists
      //     const viewer = viewerRef.current?.cesiumElement;
      //     if (viewer && window.Cesium) {
      //       const Ces = Cesium;
      //       const maxTs = Math.max(...timestampLayers.map(l => l.ts));
      //       const endDate = new Date(startDate.getTime() + maxTs * 1000);
      //       viewer.clock.startTime = Ces.JulianDate.fromDate(startDate);
      //       viewer.clock.stopTime = Ces.JulianDate.fromDate(endDate);
      //       viewer.clock.currentTime = Ces.JulianDate.fromDate(startDate);
      //       try {
      //         if (viewer.timeline && typeof viewer.timeline.zoomTo === 'function') {
      //           viewer.timeline.zoomTo(viewer.clock.startTime, viewer.clock.stopTime);
      //         }
      //       } catch (e) {
      //         console.warn('timeline zoom failed', e);
      //       }
      //     }
      //     dispatchMessage({ type: 'ADD_MESSAGE', message: { type: 'text', role: 'agent', data: `Timestamps shown (start ${startISO})` } });
      //   } catch (e) {
      //     console.warn('showTimestamps failed', e);
      //   }
      //   break;

        
        // try {
        //   const viewer = viewerRef.current.cesiumElement;
        //   // Clear any previous interval
        //   if (window.timestampInterval) {
        //     clearInterval(window.timestampInterval);
        //   }
        //   for (const layer of timestampLayers) {
        //     if (timestampLayerSources[layer.ts]) {
        //       timestampLayerSources[layer.ts].forEach(dataSource => {
        //         viewer.dataSources.remove(dataSource);
        //       });
        //     }
        //   }
        //   let index = 0;
        //   window.timestampInterval = setInterval(() => {
        //     // Hide all layers
        //     // for (const layer of timestampLayers) {
        //     //   if (timestampLayerSources[layer.ts]) {
        //     //     timestampLayerSources[layer.ts].forEach(dataSource => {
        //     //       viewer.dataSources.remove(dataSource);
        //     //     });
        //     //   }
        //     // }
        //     // Show the current layer
        //     const currentLayer = timestampLayers[index];
        //     if (timestampLayerSources[currentLayer.ts]) {
        //       timestampLayerSources[currentLayer.ts].forEach(dataSource => {
        //         viewer.dataSources.add(dataSource);
        //         // viewer.dataSources.remove(dataSource);
        //       });
        //     }
        //     let prevIndex = index === 0 ? timestampLayers.length - 1 : index - 1;
        //     const prevLayer = timestampLayers[prevIndex];
        //     if (timestampLayerSources[prevLayer.ts]) {
        //       timestampLayerSources[prevLayer.ts].forEach(dataSource => {
        //         // viewer.dataSources.add(dataSource);
        //         viewer.dataSources.remove(dataSource);
        //       });
        //     }
        //     setTimestampLayerVisibility(
        //       Object.fromEntries(timestampLayers.map(l => [l.ts, l.ts === currentLayer.ts]))
        //     );
        //     setCurrentTimestampLayerIndex(index);
        //     index = (index + 1) % timestampLayers.length;
        //   }, 1000);
        // } catch (e) {
        //   console.warn('showTimestamps failed', e);
        // }
        break;
      case 'enableNominalRoutes':
        console.log('[enableNominalRoutes] Called with enabled:', a.enabled);
        if (typeof a.enabled !== 'undefined') {
          setShowHelicopterEvacRoutes(a.enabled);
          // Show/hide the nominal routes (ts: 0)
          if (a.enabled) {
            setShowHelicopterRoutesLayerBox(true);
            // Set to null to show "No Flooding" position on slider
            setCurrentHelicopterRoutesLayerIndex(null);
            console.log('[enableNominalRoutes] Set helicopter index to null (No Flooding)');
            // Use setTimeout to ensure layer box is rendered before setting visibility
            setTimeout(() => {
              setHelicopterRoutesLayerVisibility(prev => ({ ...prev, [0]: true }));
            }, 100);
          } else if (a.hideLayers) {
            setHelicopterRoutesLayerVisibility(
              Object.fromEntries(helicopterRoutesLayers.map(l => [l.ts, false]))
            );
          }
        }
      //   if (typeof a.enabled !== 'undefined') {
      //     //make routes at index zero visible
      //     const viewer = viewerRef?.current?.cesiumElement;
      //     console.debug("enableNominalRoutes called", { a, viewer });
      //     const ds = helicopterRoutesLayerSources[0];
      //     console.debug("helicopterRoutesLayerSources[0]", ds);
      //     console.debug("helicopterRoutesLayerSources", helicopterRoutesLayerSources);
      //     if (!viewer) {
      //       console.error("Cesium viewer is not available");
      //     }
      //     if (!ds) {
      //       console.error("DataSource for helicopter route 0 is undefined");
      //     }
      //     // const exists = viewer.dataSources.contains(ds);
      //     if (ds && viewer) {
      //       try {
      //         if (a.enabled) {
      //           console.debug("Adding DataSource", ds);
      //           viewer.dataSources.add(ds);
      //         } else { 
      //           console.debug("Removing DataSource", ds);
      //           viewer.dataSources.remove(ds);
      //         }
      //         setRoutesLayerVisibility((prev) => ({ ...prev, [0]: !!a.enabled }));
      //       } catch (err) {
      //         console.error("Error adding/removing DataSource", err);
      //       }
      //     }
      //   }
        break;
      case 'enableSuboptimalRoute':
        console.log('[enableSuboptimalRoute] Called with enabled:', a.enabled, 'currentTimestampLayerIndex:', currentTimestampLayerIndex);
        if (typeof a.enabled !== 'undefined') {
          setShowHelicopterEvacRoutes(a.enabled);
          console.log('[enableSuboptimalRoute] Helicopter routes visibility set to:', a.enabled);

          // When enabling, sync helicopter routes to current flood timestamp
          if (a.enabled) {
            console.log('[enableSuboptimalRoute] Setting helicopter index to match flood index:', currentTimestampLayerIndex);
            setCurrentHelicopterRoutesLayerIndex(currentTimestampLayerIndex);
            // Show the corresponding helicopter route layer
            if (currentTimestampLayerIndex < helicopterRoutesLayers.length) {
              setHelicopterRoutesLayerVisibility(
                Object.fromEntries(helicopterRoutesLayers.map((l, idx) => [l.ts, idx === currentTimestampLayerIndex]))
              );
              console.log('[enableSuboptimalRoute] Set helicopter layer visibility for index:', currentTimestampLayerIndex);
            }
          }

          // Optional: hide all helicopter route layers when disabled
          if (!a.enabled && a.hideLayers) {
            setHelicopterRoutesLayerVisibility(
              Object.fromEntries(helicopterRoutesLayers.map(l => [l.ts, false]))
            );
          }
        }
        break;
      case 'enableShowTallBuildings':
        if (typeof a.enabled !== 'undefined') {
          setShowTallBuildings(a.enabled);
          // Also show the first route (ts: 3600) to display buildings routes
          if (a.enabled && a.showRoutes) {
            setRoutesLayerVisibility(prev => ({ ...prev, [3600]: true }));
          }
          // Toggle OSM building visibility programmatically
          if (a.enabled && cesiumControlsRef.current) {
            cesiumControlsRef.current.toggleOSMBuildingVisibility();
          }
        }
        break;
      case 'enableShowTallBuildingsRoutes':
        if (typeof a.enabled !== 'undefined') {
          setShowTallBuildings(a.enabled);
          // Optional: hide all route layers when disabled
          if (!a.enabled && a.hideLayers) {
            setRoutesLayerVisibility(
              Object.fromEntries(routesLayers.map(l => [l.ts, false]))
            );
          }
        }
        break;
      case 'setTimestampIndex':
        console.log('[setTimestampIndex] Called with index:', a.index);
        // Jump to a specific timestamp index (0 = none, 1-9 = timesteps)
        if (typeof a.index === 'number' && a.index >= 0 && a.index <= timestampLayers.length) {
          // Stop any active animation
          stopTimestampAnimation();

          if (a.index === 0) {
            // Index 0 means "No Flooding" - hide all timestamp layers
            console.log('[setTimestampIndex] Setting to 0 (No Flooding)');
            setCurrentTimestampLayerIndex(null);
            setTimestampLayerVisibility(
              Object.fromEntries(timestampLayers.map(l => [l.ts, false]))
            );
            // Also update helicopter routes to match - use null to indicate "No Flooding" state
            setCurrentHelicopterRoutesLayerIndex(null);
            console.log('[setTimestampIndex] Set both indices to null (No Flooding)');
          } else {
            // Show only the specified timestamp layer (index 1-9 corresponds to array indices 0-8)
            const arrayIndex = a.index - 1;
            console.log('[setTimestampIndex] arrayIndex:', arrayIndex, 'showHelicopterEvacRoutes:', showHelicopterEvacRoutes);
            if (arrayIndex < timestampLayers.length) {
              setCurrentTimestampLayerIndex(arrayIndex);
              setTimestampLayerVisibility(
                Object.fromEntries(timestampLayers.map((l, idx) => [l.ts, idx === arrayIndex]))
              );
              // Only update helicopter routes if they are currently visible
              if (showHelicopterEvacRoutes) {
                setCurrentHelicopterRoutesLayerIndex(arrayIndex);
                console.log('[setTimestampIndex] Set currentHelicopterRoutesLayerIndex to:', arrayIndex);
                setHelicopterRoutesLayerVisibility(
                  Object.fromEntries(helicopterRoutesLayers.map((l, idx) => [l.ts, idx === arrayIndex]))
                );
                console.log('[setTimestampIndex] Updated helicopter layer visibility');
              } else {
                console.log('[setTimestampIndex] Helicopter routes NOT visible, skipping index and layer update');
              }
            }
          }
        }
        break;
      default:
        console.log('[walkthrough ui] unhandled action', a);
    }
  };

  // Reset all walkthrough state back to initial values
  const resetWalkthroughState = () => {
    // Clear chat messages
    dispatchMessage({ type: 'SET_MESSAGES', messages: [] });

    // Reset UI visibility
    setShowBubble(false);
    setShowLayerBox(false);
    setShowFolder(false);
    setShowTimestampLayerBox(true);
    setShowRoutesLayerBox(true);
    setShowHelicopterRoutesLayerBox(true);

    // Reset visual effects
    setCloudsEnabled(false);
    setRainEnabled(false);
    setWindEnabled(false);
    setGpuWindEnabled(false);
    setShowCloudsBox(false);
    setShowRainBox(false);
    setShowWindBox(false);
    setShowGpuWindBox(false);

    // Reset layer visibility
    setTimestampLayerVisibility(Object.fromEntries(timestampLayers.map(l => [l.ts, false])));
    setRiverWatermaskLayerVisibility(Object.fromEntries(riverWatermaskLayers.map(l => [l.ts, false])));
    setRoutesLayerVisibility(Object.fromEntries(routesLayers.map(l => [l.ts, false])));
    setHelicopterRoutesLayerVisibility(Object.fromEntries(helicopterRoutesLayers.map(l => [l.ts, false])));

    // Reset feature toggles
    setShowFloodTimestamps(false);
    setShowHelicopterEvacRoutes(false);

    // Reset slider indices to null (No Flooding state)
    setCurrentTimestampLayerIndex(null);
    setCurrentHelicopterRoutesLayerIndex(null);

    // Disable buildings in Cesium if they were enabled during walkthrough
    if (showTallBuildings && cesiumControlsRef.current?.toggleOSMBuildingVisibility) {
      cesiumControlsRef.current.toggleOSMBuildingVisibility();
    }
    setShowTallBuildings(false);

    // Reset camera to zoomed-out globe view showing Europe
    flyToLocation([50.5, 4.5], 3000000, {
      heading: 0,
      pitch: -Math.PI / 2,
      roll: 0,
    });

    console.log("Walkthrough state reset complete");
  };

  useEffect(() => {
    document.title = "ECHO";
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // TEMP DISABLED
    // const storedSessionKey = localStorage.getItem("sessionKey");
    // if (storedSessionKey) {
    //   setSessionKey(storedSessionKey);
    // } else {
    //   fetch("http://echodemo.com/api/start_session", { credentials: "include" })
    //     .then(res => res.json())
    //     .then(data => {
    //       localStorage.setItem("sessionKey", data.session_key);
    //       setSessionKey(data.session_key);
    //     })
    //     .catch(console.error);
    // }

    // This function connects the client to the server over a websocket.
    // 1. It will first check if there was an override get param in the URL, if so it will use that session key.
    // 2. If not, it will check if there is a session key stored in local storage, if so it will use that.
    // 3. Failing both of those, it will create a new session key and store it in local storage. 

    // overriding
    const urlParams = new URLSearchParams(window.location.search);
    const overrideValue = urlParams.get("override") || urlParams.get("profile");
    const overrideProjectValue = urlParams.get("project");

    // past user
    const storedSessionKey = localStorage.getItem("sessionKey");

    // TEMPORARILY DISABLED FOR DEMO - No backend API needed
    // Set a dummy session key for demo mode
    setSessionKey("demo-session-key");

    // if (overrideValue) {
    //   fetch(`http://echodemo.com/api/start_session?override=${overrideValue}`, {
    //     credentials: "include",
    //   })
    //     .then((res) => res.json())
    //     .then((data) => {


    //       // override local session with new override
    //       localStorage.setItem("sessionKey", data.session_key);
    //       setSessionKey(data.session_key);

    //       // set up saved projects
    //       if (data.projects) {
    //         setProjects(data.projects)
    //       }

    //       // if specific project
    //       console.log('project override', overrideProjectValue)
    //       // if (overrideProjectValue) {
    //       //   WebSocketService.sendMessage({
    //       //     "type": "server_instruction",
    //       //     "function": "request_history",
    //       //     "data": overrideProjectValue // possible code injection!!
    //       //   });
    //       //   // setShowFolder(false);
    //       //   setShowBubble(true);
    //       // }

    //     })
    //     .catch(console.error);
    // } else if (storedSessionKey) {
    //   setSessionKey(storedSessionKey);
    //   fetch("http://echodemo.com/api/start_session", { credentials: "include" })
    //     .then(res => res.json())
    //     .then(data => {

    //       // set up saved projects
    //       if (data.projects) {
    //         setProjects(data.projects)
    //       }


    //     })
    //     .catch(console.error);
    // } else {
    //   fetch("http://echodemo.com/api/start_session", { credentials: "include" })
    //     .then(res => res.json())
    //     .then(data => {
    //       localStorage.setItem("sessionKey", data.session_key);
    //       setSessionKey(data.session_key);
    //     })
    //     .catch(console.error);
    // }

    // fetch("http://echodemo.com/api/start_session?override=walsh", { credentials: "include" })
    //   .then(res => res.json())
    //   .then(data => {
    //     localStorage.setItem("sessionKey", data.session_key);
    //     setSessionKey(data.session_key);
    //   })
    //   .catch(console.error);
  }, []);

  useEffect(() => {
    if (!sessionKey) return;

    // TEMPORARILY DISABLED FOR DEMO - No backend API/WebSocket needed
    // const handleWsUpdate = (websocket) => {
    //   setWs(websocket);
    // };
    // WebSocketService.addCallback(handleWsUpdate);
    // WebSocketService.connect(sessionKey);

    // // Fetch available tools
    // fetch("http://echodemo.com/api/get_tools", { credentials: "include" })
    //   .then((res) => res.json())
    //   .catch(console.error);

    // return () => WebSocketService.removeCallback(handleWsUpdate);
  }, [sessionKey]);

  return (
    <div className="App">
      <div id="chart" ref={viewerRef} style={{ width: '100%', height: '100vh' }}>

        {/* Walkthrough intro screens handled by walkthrough logic below */}

        {/* /* Map Control Buttons */}
        <CesiumControls
          ref={cesiumControlsRef}
          viewerRef={viewerRef}
          showLayerBox={showLayerBox}
          setShowLayerBox={setShowLayerBox}
          setShowBubble={setShowBubble}
          setShowFolder={setShowFolder}
          //loadProject
          layerList={layerList}
          layerDate={layerDate}
          setLayerProgress={setLayerProgress}
          setFormattedLayers={setFormattedLayers}
          //LayerBox
          formattedLayers={formattedLayers}
          layerProgress={layerProgress}
          //TimestampLayerBox
          showTimestampLayerBox={showTimestampLayerBox}
          setShowTimestampLayerBox={setShowTimestampLayerBox}
          timestampLayers={timestampLayers}
          timestampLayerSources={timestampLayerSources}
          setTimestampLayerSources={setTimestampLayerSources}
          timestampLayerVisibility={timestampLayerVisibility}
          setTimestampLayerVisibility={setTimestampLayerVisibility}
          currentTimestampLayerIndex={currentTimestampLayerIndex}
          stopTimestampAnimation={stopTimestampAnimation}
          riverWatermaskLayers={riverWatermaskLayers}
          riverWatermaskLayerSources={riverWatermaskLayerSources}
          setRiverWatermaskLayerSources={setRiverWatermaskLayerSources}
          riverWatermaskLayerVisibility={riverWatermaskLayerVisibility}
          setRiverWatermaskLayerVisibility={setRiverWatermaskLayerVisibility}
          //RoutesLayerBox
          showRoutesLayerBox={showRoutesLayerBox}
          setShowRoutesLayerBox={setShowRoutesLayerBox}
          routesLayers={routesLayers}
          routesLayerSources={routesLayerSources}
          setRoutesLayerSources={setRoutesLayerSources}
          routesLayerVisibility={routesLayerVisibility}
          setRoutesLayerVisibility={setRoutesLayerVisibility}
          // Helicopter evac routes
          showHelicopterRoutesLayerBox={showHelicopterRoutesLayerBox}
          setShowHelicopterRoutesLayerBox={setShowHelicopterRoutesLayerBox}
          helicopterRoutesLayers={helicopterRoutesLayers}
          helicopterRoutesLayerSources={helicopterRoutesLayerSources}
          setHelicopterRoutesLayerSources={setHelicopterRoutesLayerSources}
          helicopterRoutesLayerVisibility={helicopterRoutesLayerVisibility}
          setHelicopterRoutesLayerVisibility={setHelicopterRoutesLayerVisibility}
          currentHelicopterRoutesLayerIndex={currentHelicopterRoutesLayerIndex}
          //CloudControlBox
          showCloudsBox={showCloudsBox}
          setShowCloudsBox={setShowCloudsBox}
          cloudsEnabled={cloudsEnabled}
          setCloudsEnabled={setCloudsEnabled}
          cloudOpacity={cloudOpacity}
          setCloudOpacity={setCloudOpacity}
          cloudThreshold={cloudThreshold}
          setCloudThreshold={setCloudThreshold}
          cloudRange={cloudRange}
          setCloudRange={setCloudRange}
          cloudSteps={cloudSteps}
          setCloudSteps={setCloudSteps}
          //RainControlBox
          showRainBox={showRainBox}
          setShowRainBox={setShowRainBox}
          rainEnabled={rainEnabled}
          setRainEnabled={setRainEnabled}
          rainIntensity={rainIntensity}
          setRainIntensity={setRainIntensity}
          rainSpeed={rainSpeed}
          setRainSpeed={setRainSpeed}
          rainBlur={rainBlur}
          setRainBlur={setRainBlur}
          //WindControlBox
          showWindBox={showWindBox}
          setShowWindBox={setShowWindBox}
          windEnabled={windEnabled}
          setWindEnabled={setWindEnabled}
          windSpeed={windSpeed}
          setWindSpeed={setWindSpeed}
          windDirection={windDirection}
          setWindDirection={setWindDirection}
          windParticleDensity={windParticleDensity}
          setWindParticleDensity={setWindParticleDensity}
          windAltitude={windAltitude}
          setWindAltitude={setWindAltitude}
          windTurbulence={windTurbulence}
          setWindTurbulence={setWindTurbulence}
          //GPUWindControlBox
          showGpuWindBox={showGpuWindBox}
          setShowGpuWindBox={setShowGpuWindBox}
          gpuWindEnabled={gpuWindEnabled}
          setGpuWindEnabled={setGpuWindEnabled}
          gpuWindSpeed={gpuWindSpeed}
          setGpuWindSpeed={setGpuWindSpeed}
          gpuWindDirection={gpuWindDirection}
          setGpuWindDirection={setGpuWindDirection}
          gpuWindParticleCount={gpuWindParticleCount}
          setGpuWindParticleCount={setGpuWindParticleCount}
          gpuWindAltitude={gpuWindAltitude}
          setGpuWindAltitude={setGpuWindAltitude}
          gpuWindTurbulence={gpuWindTurbulence}
          setGpuWindTurbulence={setGpuWindTurbulence}
          gpuWindParticleLifetime={gpuWindParticleLifetime}
          setGpuWindParticleLifetime={setGpuWindParticleLifetime}
          gpuWindParticleSize={gpuWindParticleSize}
          setGpuWindParticleSize={setGpuWindParticleSize}
          //
          showFloodTimestamps={showFloodTimestamps}
          setShowFloodTimestamps={setShowFloodTimestamps}
          showHelicopterEvacRoutes={showHelicopterEvacRoutes}
          setShowHelicopterEvacRoutes={setShowHelicopterEvacRoutes}
          showTallBuildings={showTallBuildings}
          setShowTallBuildings={setShowTallBuildings}
          //Walkthrough
          currentWalkthroughStep={currentWalkthroughStep}
        />

        {/* Disabled Buttons but Cesium gets loaded here */}
        <TerrainButtons
          viewerRef={viewerRef}
        />
          <WalkthroughProvider handlers={{
            dispatchMessage,
            setShowBubble,
            flyTo: flyToLocation,
            setChatState: (chatState) => dispatchMessage({ type: 'SET_MESSAGES', messages: chatState }),
            ui: handleUiAction,
            onReset: resetWalkthroughState,
            setCurrentStep: setCurrentWalkthroughStep,
          }}>
            <WalkthroughUI
              externalPrompt={externalPrompt}
              messages={messages}
              dispatchMessage={dispatchMessage}
              currentProject={currentProject}
              flyToLocation={flyToLocation}
              setShowBubble={setShowBubble}
            />
          </WalkthroughProvider>

      </div>
    </div >
  );
}

export default App;

// Inner component which can safely call useWalkthrough() because it's
// rendered inside the WalkthroughProvider above.
function WalkthroughUI({ externalPrompt, messages, dispatchMessage, currentProject, flyToLocation, setShowBubble }) {
  const { currentStep, currentStepIndex, steps, next, reset } = useWalkthrough();
  const isLastStep = currentStepIndex === steps.length - 1;

  const renderIntroContent = () => {
    const compKey = currentStep?.ui.contentComponent;
    const compProps = currentStep?.ui.contentProps || {};
    if (compKey && componentRegistry[compKey]) {
      const Component = componentRegistry[compKey];
      return <Component {...compProps} onNext={next} />;
    }
    return <div style={{ color: 'white', fontSize: '2rem', textShadow: '0 2px 8px #000' }}>{currentStep?.ui.content}</div>;
  };

  return (
    <>
      {currentStep && currentStep.type === 'intro' && (
        <IntroScreen
          backgroundImage={currentStep.ui?.backgroundImage}
          content={renderIntroContent()}
          buttonText={currentStep.ui?.buttonText || 'Next'}
          onNext={next}
          onReset={reset}
          isLastStep={isLastStep}
        />
      )}

      {currentStep && Array.isArray(currentStep.ui?.showComponents) && currentStep.ui.showComponents.includes("SpeechBubbleChat") && (
        <SpeechBubbleChat
          externalPrompt={externalPrompt}
          messages={messages}
          dispatchMessage={dispatchMessage}
          currentProject={currentProject}
          flyToLocation={flyToLocation}
          setShowBubble={setShowBubble}
        />
      )}

      {currentStep && Array.isArray(currentStep.ui?.showComponents) && currentStep.ui.showComponents.includes("LoadingScreen") && (
        <div className="loading-screen">Loading...</div>
      )}
    </>
  );
}